import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { cn } from "@/lib/utils";

// ── Helpers ──────────────────────────────────────────────────

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

function randomFrom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

// ── Emoji Interpretation System ──────────────────────────────

interface EmojiReading {
  constellation: string;
  interpretations: string[];
}

const EMOJI_READINGS: Record<string, EmojiReading> = {
  "🌅": {
    constellation: "The Golden Farewell",
    interpretations: [
      "The sky put on its best dress just as the day was leaving. You noticed. Most don't.",
      "Every ending is just the universe getting ready for its next trick. You caught the in between.",
      "The sun takes a bow and you were there with colors in hand. Splendid.",
    ],
  },
  "🌄": {
    constellation: "The First Whisper",
    interpretations: [
      "You woke up before the world and found it blushing. The mountains are in on the secret too.",
      "Dawn is the sky trying on colors before anyone is watching. But you were watching.",
    ],
  },
  "☀️": {
    constellation: "The Generous Star",
    interpretations: [
      "The sun gives its light to everything equally. Flowers, puddles, parking lots. You have that energy.",
      "You chose the one star that never asks for applause. Confidence looks good on you.",
    ],
  },
  "🌙": {
    constellation: "The Quiet Lantern",
    interpretations: [
      "The moon borrows its light and somehow makes it more beautiful. You understand that magic.",
      "Night shows things differently. You have always preferred that version.",
      "The moon is up there right now doing absolutely nothing and everyone loves it. There is a lesson in that.",
    ],
  },
  "⭐": {
    constellation: "The Steady Wink",
    interpretations: [
      "One little star twinkling away for millions of years. That is commitment. That is you.",
      "A single star once led travelers home. Yours might do the same for someone scrolling by.",
    ],
  },
  "✨": {
    constellation: "The Scattered Joy",
    interpretations: [
      "You sprinkled magic on it! The universe appreciates a generous hand with the glitter.",
      "Sparkles are the cosmos reminding you that light loves to play. And you let it.",
    ],
  },
  "💫": {
    constellation: "The Dizzy Delight",
    interpretations: [
      "A star in motion! You are seeing everything at once. That is a gift.",
      "The shooting star does not know where it is going either. It just trusts the sparkle. So should you.",
    ],
  },
  "🔥": {
    constellation: "The Bright Dare",
    interpretations: [
      "Fire does not ask permission to dance. You picked it because something in you is ready to leap.",
      "Every campfire was once a single brave spark. You just lit something wonderful.",
      "The warmest people always carry a little fire in their pockets. I see yours.",
    ],
  },
  "🌊": {
    constellation: "The Wandering Deep",
    interpretations: [
      "The ocean never visits the same shore twice the same way. You love that about it and about yourself.",
      "Waves never worry about where they have been. They just keep arriving, full and generous. Like you.",
    ],
  },
  "🌸": {
    constellation: "The Joyful Fleeting",
    interpretations: [
      "The blossom knows its time is short and it blooms anyway. Extravagantly. You understand this courage.",
      "Pink petals on the wind. You chose something that celebrates right now, not forever. How wise.",
    ],
  },
  "🍂": {
    constellation: "The Graceful Drift",
    interpretations: [
      "A falling leaf is just a tree waving goodbye. You see the beauty in letting go.",
      "Autumn puts on its most spectacular outfit before the curtain falls. You have that same flair.",
    ],
  },
  "🌿": {
    constellation: "The Quiet Persistence",
    interpretations: [
      "Green just keeps going. No drama, no fanfare. Just steady, stubborn life. You carry that same magic.",
      "A leaf does not announce its growth. It simply grows. There is something deeply you about that.",
    ],
  },
  "🌈": {
    constellation: "The Whole Spectrum",
    interpretations: [
      "You chose ALL the colors! The sky only does this after a storm. You have earned this.",
      "The rainbow is light showing off. You picked the cosmic encore. Bold and beautiful.",
    ],
  },
  "💎": {
    constellation: "The Patient Sparkle",
    interpretations: [
      "Diamonds are just rocks that did not give up. You recognize that story because it is yours too.",
      "Light bends itself into rainbows inside this little stone. You chose concentrated wonder.",
    ],
  },
  "🎨": {
    constellation: "The Open Canvas",
    interpretations: [
      "The palette holds every possibility at once! You did not choose a color. You chose potential itself.",
      "An artist's tool for a color moment. You are thinking in layers. The stars appreciate recursion.",
    ],
  },
  "🌺": {
    constellation: "The Warm Welcome",
    interpretations: [
      "This flower opens like it is greeting the entire world. You carry that same warmth.",
      "Tropical bloom. You brought sunshine from somewhere the rest of us can only dream about.",
    ],
  },
  "🍃": {
    constellation: "The Happy Surrender",
    interpretations: [
      "A leaf on the wind is not lost. It is on the most exciting ride of its life. You know that feeling.",
      "Letting go is trusting that the breeze knows where the party is.",
    ],
  },
  "❄️": {
    constellation: "The Tiny Cathedral",
    interpretations: [
      "Every snowflake is a masterpiece that lasts for seconds. You chose the universe's favorite art form.",
      "Perfectly still, perfectly formed, perfectly brief. Like this moment.",
    ],
  },
  "🌻": {
    constellation: "The Loyal Turner",
    interpretations: [
      "The sunflower always knows where the light is. You have that same compass inside you. Trust it.",
      "Turning toward warmth is the oldest and wisest instinct there is.",
    ],
  },
  "🦋": {
    constellation: "The Beautiful Becoming",
    interpretations: [
      "You were goo! Actual goo! And now look at you. Wings and everything. The stars are impressed.",
      "The butterfly does not remember the cocoon. It just flies. You are already flying.",
    ],
  },
  "🕯️": {
    constellation: "The Little Keeper",
    interpretations: [
      "A tiny flame keeping watch. You chose the smallest, bravest light.",
      "The candle lights what is nearest. You understand that changing the world starts with this room.",
    ],
  },
  "🪐": {
    constellation: "The Ringed Wonder",
    interpretations: [
      "Saturn wears its history like jewelry! All those rings, every one a chapter. You appreciate a good story.",
      "A planet with accessories. You chose the celestial body with the most personality. Of course you did.",
    ],
  },
  "🫧": {
    constellation: "The Floating Miracle",
    interpretations: [
      "A bubble is a tiny rainbow that decided to go for a walk. You chose joy in its most delicate form.",
      "It exists for a moment, catches every color, and pops with a grin. Life goals, honestly.",
    ],
  },
  "🌑": {
    constellation: "The Gathering Dark",
    interpretations: [
      "The new moon is not missing. It is backstage getting ready for something spectacular.",
      "You chose the pause between breaths. Everything wonderful begins in that quiet dark.",
    ],
  },
  "🐚": {
    constellation: "The Curling Path",
    interpretations: [
      "Hold it to your ear. That is not the ocean. That is the sound of everywhere you have ever been.",
      "Spirals, not straight lines. You know the scenic route is always the real route.",
    ],
  },
  "💜": {
    constellation: "The Velvet Heart",
    interpretations: [
      "Purple hearts are for the romantics who also read philosophy. You contain multitudes.",
      "You chose a heart! The bravest, silliest, most wonderful symbol. It says I am wide open.",
    ],
  },
  "❤️": {
    constellation: "The Original Warmth",
    interpretations: [
      "Red and unguarded. You wear your heart like a lantern and the world is brighter for it.",
      "The heart was the first thing your body built. You chose the beginning of everything.",
    ],
  },
  "🖤": {
    constellation: "The Deep Well",
    interpretations: [
      "A black heart is not empty. It is full of everything it has ever loved. That is a lot of love in there.",
      "Deep and full. You carry more magic than you show. The stars see it all though.",
    ],
  },
  "🤍": {
    constellation: "The Open Page",
    interpretations: [
      "White holds every color in perfect balance. You chose the most generous, peaceful possibility.",
      "A blank page is not empty. It is infinitely full of everything that has not been written yet.",
    ],
  },
  "🧿": {
    constellation: "The Watchful Friend",
    interpretations: [
      "The eye that looks out for you! You believe in protection and that belief itself is a kind of magic.",
      "A little blue guardian. The best things in life are the ones that watch over you quietly.",
    ],
  },
};

// ── Category Fallbacks ───────────────────────────────────────
// Emojis not in EMOJI_READINGS get matched by category group.

const CATEGORY_READINGS: { emojis: string[]; reading: EmojiReading }[] = [
  {
    emojis: [
      "⛅",
      "☁️",
      "🌕",
      "🌗",
      "💧",
      "⚡",
      "🌪️",
      "🌫️",
      "💨",
      "🌡️",
      "⛱️",
      "⛄",
      "☔",
      "🌀",
      "🌬️",
    ],
    reading: {
      constellation: "The Sky's Mood",
      interpretations: [
        "You chose the weather itself. The sky is flattered that you noticed what it was wearing today.",
        "Most people complain about weather. You turned it into art. The clouds are blushing.",
        "The atmosphere dressed up and you took a picture. That is the kindest thing anyone has done for it.",
        "Weather is just the sky having feelings. You noticed which feeling. That is empathy on a cosmic scale.",
      ],
    },
  },
  {
    emojis: ["⏳", "⏰", "🧭", "🗺️", "🌍", "🌎"],
    reading: {
      constellation: "The Wayfinder",
      interpretations: [
        "You chose something that points the way. The stars think you already know where you are going.",
        "Time and direction. You picked a symbol that knows the universe is always moving. So are you.",
        "Maps and clocks. You chose the tools of someone who takes the journey seriously. The stars respect that.",
        "Every compass needle is just a tiny magnet reaching for home. You understand that pull.",
      ],
    },
  },
  {
    emojis: [
      "💚",
      "💙",
      "💛",
      "🧡",
      "💖",
      "💗",
      "💓",
      "💕",
      "❤️‍🔥",
      "❤️‍🩹",
      "💘",
      "💝",
      "💞",
      "❣️",
      "🩵",
    ],
    reading: {
      constellation: "The Open Heart",
      interpretations: [
        "You chose a heart. Every heart is brave. Yours is braver than most for being here.",
        "A heart in color. You are not afraid to feel out loud. The stars see that and they are cheering.",
        "Love in a little symbol. You managed to fit the biggest feeling into the smallest shape.",
        "The heart beats without being asked. You chose the most generous muscle in the universe.",
      ],
    },
  },
  {
    emojis: ["🌷", "🌹", "🌼", "💐", "🍀", "🍁", "🌾", "🪴", "🪵", "🌱", "🪨"],
    reading: {
      constellation: "The Growing Thing",
      interpretations: [
        "Something alive and reaching for the light. You chose growth, and growth is always quiet and always brave.",
        "From the earth, upward. You picked a symbol that knows the secret: start small, keep going.",
        "Every garden was once just dirt with ambition. You see the ambition part. That is the gift.",
        "Green things do not rush. They just show up every morning and try again. You have that same patience.",
      ],
    },
  },
  {
    emojis: ["🌲", "🌳", "🌴", "🌵", "🍄", "🪻", "🪷", "🪹"],
    reading: {
      constellation: "The Rooted One",
      interpretations: [
        "Tall things that stay where they are planted. You admire steadiness. The forest approves.",
        "Roots and branches. You chose something that grows in two directions at once. Up and down. Like you.",
        "Trees talk to each other underground. You chose the original social network.",
        "Standing tall for a very long time. That is not stubbornness. That is faith.",
      ],
    },
  },
  {
    emojis: ["🐱", "🐶"],
    reading: {
      constellation: "The Loyal Companion",
      interpretations: [
        "You chose a friend. The best kind. The kind that does not need words to understand you.",
        "Soft ears and warm eyes. You picked unconditional love. The stars think that is very wise.",
        "A creature that is happy just because you walked into the room. You chose pure joy.",
        "Warm nose, full heart. The simplest and most perfect constellation in the sky.",
      ],
    },
  },
  {
    emojis: [
      "🦊",
      "🦌",
      "🐺",
      "🦁",
      "🐯",
      "🐻",
      "🐻‍❄️",
      "🐘",
      "🦒",
      "🦓",
      "🐆",
      "🐪",
      "🦬",
      "🐵",
      "🦍",
      "🦝",
      "🐨",
      "🐼",
      "🦛",
      "🦏",
      "🐎",
      "🐑",
      "🐐",
      "🦘",
      "🐹",
      "🦡",
      "🦨",
      "🐗",
      "🫎",
      "🫏",
      "🦙",
    ],
    reading: {
      constellation: "The Wild Soul",
      interpretations: [
        "You picked a creature with its own rules. Something wild lives in you too. The stars can see it.",
        "An animal that answers to no one. You chose instinct over instruction. That takes courage.",
        "Wild things do not worry about what other wild things think of them. You chose that freedom.",
        "Somewhere out there this creature is doing exactly what it wants right now. You admire that. So do we.",
      ],
    },
  },
  {
    emojis: [
      "🐢",
      "🦎",
      "🐇",
      "🦔",
      "🐿️",
      "🦦",
      "🦥",
      "🦫",
      "🐾",
      "🪺",
      "🪸",
      "🦧",
    ],
    reading: {
      constellation: "The Small Wonder",
      interpretations: [
        "Tiny and wonderful. You chose a creature that proves you do not need to be big to matter.",
        "Small paws, big spirit. The stars have a soft spot for the little ones. So do you.",
        "Little creatures carry the whole world on their backs and never mention it. You noticed.",
        "The smallest constellation in the sky and somehow the most loved. Like the creature you chose.",
      ],
    },
  },
  {
    emojis: ["🐊", "🐍", "🐉", "🦕", "🦖", "🐸"],
    reading: {
      constellation: "The Ancient Scale",
      interpretations: [
        "Something older than most stars. You chose a creature that was here before the constellations had names.",
        "Cold blooded, warm hearted. You picked something ancient and patient. The universe respects that.",
        "Millions of years of knowing exactly what to do. You chose deep wisdom wearing a funny outfit.",
        "The oldest stories all start with something like this. You are beginning at the beginning.",
      ],
    },
  },
  {
    emojis: [
      "🐝",
      "🐞",
      "🦩",
      "🦚",
      "🦜",
      "🕊️",
      "🦅",
      "🦆",
      "🦢",
      "🦉",
      "🐦",
      "🐧",
      "🐔",
      "🐤",
      "🐣",
      "🪿",
      "🦤",
      "🪶",
    ],
    reading: {
      constellation: "The Feathered Sky",
      interpretations: [
        "Wings! You chose something that knows what the sky looks like from the inside. The stars are jealous.",
        "A creature of the air. You picked flight, and flight is just trust with feathers.",
        "Birds sing before dawn because they believe the sun will come. You chose that optimism.",
        "Feathers are just colors that learned to fly. You picked a living rainbow with opinions.",
      ],
    },
  },
  {
    emojis: [
      "🐠",
      "🐬",
      "🦈",
      "🐙",
      "🦑",
      "🪼",
      "🦀",
      "🐳",
      "🦭",
      "🐟",
      "🦐",
      "🐡",
      "🦞",
      "🦪",
    ],
    reading: {
      constellation: "The Deep Current",
      interpretations: [
        "Something from below the surface. You chose a creature that lives where light has to work harder to reach.",
        "The ocean keeps its best residents hidden. You found one anyway. The depths are impressed.",
        "Down where the water is dark and the pressure is real, beautiful things still thrive. Like you.",
        "The sea has more colors than the sky. You chose the gallery most people never visit.",
      ],
    },
  },
  {
    emojis: [
      "🐛",
      "🐜",
      "🐌",
      "🦟",
      "🦗",
      "🪲",
      "🪳",
      "🕷️",
      "🕸️",
      "🦂",
      "🦠",
      "🪱",
    ],
    reading: {
      constellation: "The Tiny Builder",
      interpretations: [
        "So small, so busy, so important. You chose a creature the world overlooks but could not survive without.",
        "The smallest things do the biggest work. You see that. Most people step right over this wisdom.",
        "A whole civilization in the dirt, building empires no one asked for. You admire the ambition.",
        "Busy and purposeful and completely unbothered by your opinion. That is a lifestyle choice the stars respect.",
      ],
    },
  },
  {
    emojis: ["☄️", "🌌", "🛸", "🔭", "🪬", "♾️", "⚛️"],
    reading: {
      constellation: "The Vast Beyond",
      interpretations: [
        "You looked past the sky and into what is behind it. The cosmos appreciates being noticed.",
        "Infinite and strange and full of wonder. You chose the mystery itself. The stars approve.",
        "Most people stop at the sky. You kept going. The universe was hoping someone would.",
        "Out there, past everything, is more everything. You chose to acknowledge that. The void waves back.",
      ],
    },
  },
  {
    emojis: ["🔮", "☯️", "✴️", "❇️", "💠"],
    reading: {
      constellation: "The Hidden Pattern",
      interpretations: [
        "Geometry and magic. You chose a shape that the universe uses when it thinks no one is looking.",
        "Patterns within patterns. You see the structure underneath the sparkle. That is a rare gift.",
        "Every crystal, every snowflake, every orbit follows a pattern. You chose the pattern itself. Very you.",
        "The universe is made of math pretending to be beautiful. You saw through the disguise.",
      ],
    },
  },
  {
    emojis: [
      "👁️",
      "🎭",
      "🎪",
      "🎠",
      "🎡",
      "🏰",
      "⛩️",
      "🗼",
      "🌉",
      "🎆",
      "⛰️",
      "🌋",
      "🏖️",
      "🏜️",
      "🏝️",
      "🗽",
      "🗿",
      "🏕️",
    ],
    reading: {
      constellation: "The Distant Place",
      interpretations: [
        "You chose somewhere to be. A place with its own gravity. The stars think you carry places inside you.",
        "A destination, a landmark, a world. You picked a spot on the map where stories happen.",
        "Every great place was once just a piece of ground that someone decided to love. You are that someone.",
        "You chose a place. The stars chose you. Somewhere in the middle is where the magic lives.",
      ],
    },
  },
  {
    emojis: ["💡", "🔦", "🪔", "🎐", "🎏", "🎀", "🎁", "🪄", "🔑", "🗝️"],
    reading: {
      constellation: "The Little Magic",
      interpretations: [
        "You chose an object that does something wonderful. Light, opening, giving, casting. Everyday sorcery.",
        "The most magical things are small and ordinary until someone picks them up. You just did.",
        "A key, a candle, a ribbon. Tiny things that change everything. You have an eye for quiet power.",
        "Wizards do not carry wands because they need them. They carry them because they are beautiful. Like this.",
      ],
    },
  },
  {
    emojis: ["🎵", "🎹", "🎸", "🥁", "🎺", "🎻", "🪘", "📯", "🎬", "🎧", "📸"],
    reading: {
      constellation: "The Living Sound",
      interpretations: [
        "Music and light and captured moments. You chose something that makes time stand still.",
        "The stars hum too, you know. You just picked the instrument that matches their key.",
        "Sound is just air that learned to dance. You chose the dance itself.",
        "Every song is someone saying what they could not say with words. You picked the feeling before the lyrics.",
      ],
    },
  },
  {
    emojis: [
      "🍎",
      "🍊",
      "🍋",
      "🍇",
      "🍓",
      "🍑",
      "🥝",
      "🍉",
      "🥭",
      "🍒",
      "🫐",
      "🍌",
      "🍈",
      "🍐",
      "🥥",
      "🥑",
      "🍅",
      "🍆",
      "🌶️",
      "🌽",
      "🥕",
      "🥦",
      "🍠",
    ],
    reading: {
      constellation: "The Ripe Moment",
      interpretations: [
        "Something the earth grew just for you. Color and sweetness in a single bite. The stars are hungry now.",
        "You chose flavor. The most honest form of beauty. It does not last and it does not need to.",
        "The earth spent months making this. Sun and rain and patience. You held up the result and said yes.",
        "Nature's color palette is edible. You chose the most delicious part of the spectrum.",
      ],
    },
  },
  {
    emojis: [
      "🍔",
      "🍕",
      "🍟",
      "🍣",
      "🍜",
      "🍝",
      "🍳",
      "🎂",
      "🍩",
      "🍪",
      "🍦",
      "🍧",
      "🍞",
      "🥐",
      "🍿",
      "🍯",
      "🧁",
      "🍰",
      "🍫",
      "🍬",
      "🍭",
    ],
    reading: {
      constellation: "The Warm Table",
      interpretations: [
        "You chose comfort. Something made with care and eaten with joy. The stars wish they could taste things.",
        "Food is love made visible. You picked a symbol that the whole universe understands.",
        "Someone made this. With their hands and their heart. You chose the warmest thing in the world.",
        "A meal is a tiny celebration. You picked the kind of beauty that fills you up from the inside.",
      ],
    },
  },
  {
    emojis: ["🧃", "🍵", "☕", "🍷", "🥂", "🧊", "🧋", "🍹", "🥛"],
    reading: {
      constellation: "The Shared Cup",
      interpretations: [
        "Something to sip. Something to share. You chose the quiet ritual of sitting still with something warm.",
        "A drink is a pause made real. You picked the moment between doing and being. The stars toast you.",
        "The best conversations happen over something in a cup. You chose the container for connection.",
        "Steam rising, warmth spreading. You picked the coziest symbol in the entire emoji catalog.",
      ],
    },
  },
  {
    emojis: ["🎉", "🎈", "🎃", "🎄", "🎋", "🎍", "🧧"],
    reading: {
      constellation: "The Bright Occasion",
      interpretations: [
        "You chose celebration! The stars love a good party. Especially one with colors.",
        "Festivity and joy. You picked a symbol that says something worth celebrating just happened. And it did.",
        "Confetti and streamers and the feeling that right now is exactly where you should be.",
        "The universe throws a party every time a star is born. You just joined the guest list.",
      ],
    },
  },
  {
    emojis: [
      "⚽",
      "🏀",
      "🎾",
      "🏆",
      "🥇",
      "🎯",
      "🎲",
      "🧩",
      "♟️",
      "🕹️",
      "🎳",
      "🎱",
    ],
    reading: {
      constellation: "The Playful Aim",
      interpretations: [
        "Games and goals. You chose something that knows the point is not winning, it is playing.",
        "Strategy or luck or just the joy of throwing something and seeing where it lands. The stars play too.",
        "The universe is one big game and you just picked up a piece. The board is yours.",
        "Play is the most serious thing in the world. Children know this. You remembered.",
      ],
    },
  },
  {
    emojis: ["🧪", "🧬", "🔬", "🧲"],
    reading: {
      constellation: "The Curious Eye",
      interpretations: [
        "You chose to look closer. Science is just wonder with better tools. The stars invented it first.",
        "Molecules and magnets and the urge to understand. You picked curiosity itself. The universe is pleased.",
        "Every great discovery started with someone saying what if. You just said it with an emoji.",
        "The microscope and the telescope both do the same thing. They show you there is always more.",
      ],
    },
  },
];

function getCategoryReading(emoji: string): EmojiReading | null {
  for (const cat of CATEGORY_READINGS) {
    if (cat.emojis.includes(emoji)) return cat.reading;
  }
  return null;
}

const GENERAL_READINGS: EmojiReading = {
  constellation: "The Uncharted",
  interpretations: [
    "This symbol is not on any chart I have seen and I love it. You have drawn a brand new constellation!",
    "The stars are squinting at this one. They have never seen it before. How exciting for all of us!",
    "An unknown glyph! The sky just made room for something new. That is because of you.",
    "Some symbols are so new they have not earned a name yet. Yours is among the first stars.",
    "The chart has no entry for this. You are writing mythology in real time. How wonderful.",
  ],
};

const NO_EMOJI_READING: EmojiReading = {
  constellation: "The Empty Sky",
  interpretations: [
    "You sent colors into the world with no symbol to guide them. The stars prepared a whole reading and everything.",
    "No emoji. The constellation was ready. The stars had rehearsed. But you came without a symbol.",
    "The sky set the stage and you walked in without a costume. Bold, honestly. A little heartbreaking.",
    "Colors without a symbol. Like a letter without a signature. Beautiful, but the stars wanted to know who sent it.",
    "The constellation waited. And waited. No symbol arrived. Next time, perhaps.",
  ],
};

const NO_EMOJI_GREETINGS = [
  "The stars were ready...",
  "A constellation stirs... and waits...",
  "The heavens look down and... oh.",
  "The cosmos squints...",
];

const NO_EMOJI_CLOSINGS = [
  "Next time, bring a symbol. The stars will be here.",
  "The constellation dims, but it will remember you.",
  "An empty chart is still a chart. Yours is waiting.",
  "The sky forgives. It always does.",
];

// ── Greetings & Closings ─────────────────────────────────────

const GREETINGS = [
  "Oh! The stars are tingling...",
  "A new constellation is forming...",
  "The heavens are delighted...",
  "Something wonderful stirs above...",
  "The cosmos perks up...",
];

const CLOSINGS = [
  "The stars are smiling.",
  "Written in light, kept in memory.",
  "The constellation hums with joy.",
  "May the sky remember this kindly.",
  "Look up tonight. It is all there, twinkling for you.",
];

// ── Star Chart Constellation Points ──────────────────────────

interface StarPoint {
  x: number;
  y: number;
  size: number;
  delay: number;
}

interface AmbientStar {
  x: number;
  y: number;
  size: number;
  delay: number;
  duration: number;
}

interface ConstellationData {
  stars: StarPoint[];
  ambient: AmbientStar[];
}

/** 4-pointed star SVG path centered at origin */
function starPath(size: number): string {
  const o = size * 0.3; // inner radius
  const s = size; // outer radius
  return `M0,${-s} L${o},${-o} L${s},0 L${o},${o} L0,${s} L${-o},${o} L${-s},0 L${-o},${-o}Z`;
}

function generateConstellation(): ConstellationData {
  const stars: StarPoint[] = [];
  const cx = 50;
  const cy = 50;
  for (let i = 0; i < 7; i++) {
    const angle = (i / 7) * Math.PI * 2 + (Math.random() - 0.5) * 0.7;
    const dist = 22 + Math.random() * 18;
    stars.push({
      x: cx + Math.cos(angle) * dist,
      y: cy + Math.sin(angle) * dist,
      size: 1.5 + Math.random() * 1.5,
      delay: i * 0.25,
    });
  }

  // Ambient twinkles scattered across the chart
  const ambient: AmbientStar[] = [];
  for (let i = 0; i < 30; i++) {
    ambient.push({
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 0.3 + Math.random() * 0.7,
      delay: Math.random() * 4,
      duration: 3 + Math.random() * 5,
    });
  }

  return { stars, ambient };
}

// ── Component ────────────────────────────────────────────────

interface AstrologerContent {
  greeting: string;
  emoji: string;
  reading: EmojiReading;
  interpretation: string;
  closing: string;
  constellation: ConstellationData;
}

interface ColorAstrologerProps {
  className?: string;
  justSubmittedEmoji?: string;
  justSubmittedColors?: string[];
  onDismiss?: () => void;
}

type AstrologerState =
  | "hidden"
  | "entering"
  | "phase1"
  | "phase2"
  | "phase3"
  | "farewell"
  | "closing";

// Preload images
const preloadImages = ["/the_astrologist.png"];
preloadImages.forEach((src) => {
  const img = new Image();
  img.src = src;
});

export function ColorAstrologer({
  className,
  justSubmittedEmoji,
  justSubmittedColors,
  onDismiss,
}: ColorAstrologerProps) {
  const [state, setState] = useState<AstrologerState>("hidden");
  const [content, setContent] = useState<AstrologerContent | null>(null);
  const [hasEverShown, setHasEverShown] = useState(false);

  // Phase 1
  const [phase1Visible, setPhase1Visible] = useState(0);

  // Phase 2: star chart
  const [chartVisible, setChartVisible] = useState(false);
  const [linesDrawn, setLinesDrawn] = useState(false);
  const [emojiVisible, setEmojiVisible] = useState(false);
  const [interpretationVisible, setInterpretationVisible] = useState(false);

  // Phase 3: reading
  const [readingVisible, setReadingVisible] = useState(false);

  // Farewell
  const [farewellVisible, setFarewellVisible] = useState(false);

  const rolledForEmojiRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Trigger — no probability, handled by gate. Empty string = no emoji (triggers disappointed path).
  useEffect(() => {
    if (justSubmittedEmoji != null && state === "hidden") {
      if (rolledForEmojiRef.current === justSubmittedEmoji) return;
      rolledForEmojiRef.current = justSubmittedEmoji;

      timerRef.current = setTimeout(() => {
        setHasEverShown(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setState("entering");
          });
        });
      }, 1800);
    }
  }, [justSubmittedEmoji, state]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const generateContent = useCallback((): AstrologerContent => {
    const emoji = justSubmittedEmoji || "";
    const noEmoji = !emoji;
    const reading = noEmoji
      ? NO_EMOJI_READING
      : EMOJI_READINGS[emoji] || getCategoryReading(emoji) || GENERAL_READINGS;
    const interpretation = randomFrom(reading.interpretations);
    return {
      greeting: randomFrom(noEmoji ? NO_EMOJI_GREETINGS : GREETINGS),
      emoji,
      reading,
      interpretation,
      closing: randomFrom(noEmoji ? NO_EMOJI_CLOSINGS : CLOSINGS),
      constellation: generateConstellation(),
    };
  }, [justSubmittedEmoji]);

  // Phase 1: fade in greeting
  useEffect(() => {
    if (state !== "phase1") {
      setPhase1Visible(0);
      return;
    }
    if (phase1Visible < 2) {
      const timer = setTimeout(
        () => setPhase1Visible((v) => v + 1),
        phase1Visible === 0 ? 600 : 1000,
      );
      return () => clearTimeout(timer);
    }
  }, [state, phase1Visible]);

  // Phase 2: star chart animation sequence — stays visible through phase 3
  useEffect(() => {
    if (state !== "phase2" && state !== "phase3") {
      setChartVisible(false);
      setLinesDrawn(false);
      setEmojiVisible(false);
      setInterpretationVisible(false);
      return;
    }

    // Only start animations on phase2 entry (not re-entering from phase3)
    if (state === "phase2") {
      const t1 = setTimeout(() => setChartVisible(true), 300);
      const t2 = setTimeout(() => setLinesDrawn(true), 800);
      const t3 = setTimeout(() => setEmojiVisible(true), 1600);
      const t4 = setTimeout(() => setInterpretationVisible(true), 2400);

      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
        clearTimeout(t4);
      };
    }
  }, [state]);

  // Phase 3: reading reveal
  useEffect(() => {
    if (state !== "phase3") {
      setReadingVisible(false);
      return;
    }
    const timer = setTimeout(() => setReadingVisible(true), 400);
    return () => clearTimeout(timer);
  }, [state]);

  // Farewell
  useEffect(() => {
    if (state !== "farewell") {
      setFarewellVisible(false);
      return;
    }
    const timer = setTimeout(() => setFarewellVisible(true), 300);
    return () => clearTimeout(timer);
  }, [state]);

  // Handlers — single handler for all clicks anywhere
  const canAdvance =
    state === "entering" ||
    (state === "phase1" && phase1Visible >= 2) ||
    (state === "phase2" && interpretationVisible) ||
    (state === "phase3" && readingVisible) ||
    (state === "farewell" && farewellVisible);

  const handleAdvance = useCallback(() => {
    if (state === "entering") {
      setContent(generateContent());
      setState("phase1");
    } else if (state === "phase1" && phase1Visible >= 2) {
      setState("phase2");
    } else if (state === "phase2" && interpretationVisible) {
      setState("phase3");
    } else if (state === "phase3" && readingVisible) {
      setState("farewell");
    } else if (state === "farewell" && farewellVisible) {
      setState("closing");
      setTimeout(() => {
        setState("hidden");
        onDismiss?.();
      }, 600);
    }
  }, [
    state,
    generateContent,
    phase1Visible,
    interpretationVisible,
    readingVisible,
    farewellVisible,
    onDismiss,
  ]);

  const isReading =
    state === "phase1" ||
    state === "phase2" ||
    state === "phase3" ||
    state === "farewell";

  // Accent color from palette
  const accentColor = useMemo(() => {
    if (!justSubmittedColors?.length) return "#a78bfa";
    let best = justSubmittedColors[0];
    let bestSat = 0;
    for (const c of justSubmittedColors) {
      const { s } = hexToHsl(c);
      if (s > bestSat) {
        bestSat = s;
        best = c;
      }
    }
    return best;
  }, [justSubmittedColors]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&display=swap');

        .astro-text {
          font-family: 'Cinzel Decorative', serif;
          font-weight: 700;
        }
        .astro-text-body {
          font-family: 'Cinzel Decorative', serif;
          font-weight: 400;
        }

        @keyframes astro-twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        @keyframes astro-glow-pulse {
          0%, 100% { opacity: 0.15; }
          50% { opacity: 0.4; }
        }
        @keyframes astro-fade-up {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes pointerPulseAstro {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.1); }
        }
        .pointer-hint-astro {
          animation: pointerPulseAstro 1.5s ease-in-out infinite;
        }

      `}</style>

      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 transition-opacity duration-700 z-[60]",
          isReading ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        style={{
          background: isReading
            ? "radial-gradient(ellipse at center, rgba(15, 5, 40, 0.88) 0%, rgba(0, 0, 0, 0.94) 100%)"
            : "rgba(0,0,0,0.7)",
        }}
        onClick={state !== "entering" ? handleAdvance : undefined}
      />

      {/* Character */}
      {hasEverShown && (
        <div
          className={cn(
            "fixed bottom-0 left-1/2 -translate-x-1/2 z-[80] select-none transition-all duration-700 ease-out",
            state === "hidden" || state === "closing"
              ? "pointer-events-none"
              : "cursor-pointer",
            className,
          )}
          style={{
            width: isReading ? "100%" : undefined,
            maxWidth: isReading ? "32rem" : undefined,
          }}
          onClick={
            state === "hidden" || state === "closing"
              ? undefined
              : handleAdvance
          }
        >
          <div
            className={cn(
              "transition-all duration-700 ease-out flex justify-center",
              state === "hidden" && "translate-y-full opacity-0",
              state === "entering" && "translate-y-[50%]",
              state === "closing" && "translate-y-full opacity-0",
              isReading && "translate-y-[30%]",
            )}
          >
            <img
              src="/the_astrologist.png"
              alt="The Astrologer"
              className={cn(
                "h-auto drop-shadow-2xl transition-all duration-700 ease-out",
                isReading
                  ? "w-full object-cover object-top"
                  : "w-72 sm:w-80 md:w-96 hover:scale-105",
              )}
              draggable={false}
            />
          </div>
        </div>
      )}

      {/* Phase 1: Greeting — high on screen where the bubble would sit */}
      {state === "phase1" && content && (
        <div className="fixed inset-x-0 bottom-[50dvh] md:bottom-[48dvh] z-[85] flex justify-center pointer-events-none">
          <div className="text-center w-[90vw] max-w-lg">
            <p
              className={cn(
                "astro-text text-indigo-200 leading-relaxed transition-opacity duration-1000 mb-2 tracking-wide",
                phase1Visible >= 1 ? "opacity-100" : "opacity-0",
              )}
              style={{ fontSize: "clamp(1rem, 3.5vw, 1.5rem)" }}
            >
              {content.greeting}
            </p>
            <p
              className={cn(
                "astro-text text-indigo-200 leading-relaxed transition-opacity duration-1000 tracking-wide",
                phase1Visible >= 2 ? "opacity-100" : "opacity-0",
              )}
              style={{ fontSize: "clamp(1rem, 3.5vw, 1.5rem)" }}
            >
              A symbol has been chosen.
            </p>
            <div
              className={cn(
                "mt-4 transition-opacity duration-300",
                canAdvance ? "opacity-100" : "opacity-0",
              )}
            >
              <span
                className="text-indigo-400 pointer-hint-astro inline-block"
                style={{ fontSize: "clamp(1rem, 3vw, 1.25rem)" }}
              >
                ✦
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Phase 2 & 3: Star chart + reading text above */}
      {(state === "phase2" || state === "phase3") && content && (
        <div className="fixed inset-x-0 bottom-[36dvh] md:bottom-[34dvh] z-[85] flex justify-center pointer-events-none">
          <div className="flex flex-col items-center w-[90vw] max-w-lg">
            {/* Phase 3: Reading text above the constellation */}
            {state === "phase3" && (
              <div
                className={cn(
                  "text-center mb-2 max-w-sm transition-opacity duration-1000",
                  readingVisible ? "opacity-100" : "opacity-0",
                )}
                style={{
                  animation: readingVisible
                    ? "astro-fade-up 0.6s ease-out"
                    : "none",
                }}
              >
                <p
                  className="astro-text-body text-indigo-200 leading-relaxed"
                  style={{ fontSize: "clamp(0.875rem, 3vw, 1.125rem)" }}
                >
                  {content.interpretation}
                </p>
              </div>
            )}

            {/* Star chart */}
            <div
              className={cn(
                "w-[60vw] max-w-xs aspect-square relative transition-opacity duration-700",
                chartVisible ? "opacity-100" : "opacity-0",
              )}
            >
              <svg
                viewBox="0 0 100 100"
                className="absolute inset-0 w-full h-full"
                style={{ overflow: "visible" }}
              >
                {/* SVG filter for dreamy glow */}
                <defs>
                  <filter
                    id="astro-star-glow"
                    x="-100%"
                    y="-100%"
                    width="300%"
                    height="300%"
                  >
                    <feGaussianBlur in="SourceGraphic" stdDeviation="2" />
                  </filter>
                  <filter
                    id="astro-line-glow"
                    x="-50%"
                    y="-50%"
                    width="200%"
                    height="200%"
                  >
                    <feGaussianBlur in="SourceGraphic" stdDeviation="0.8" />
                  </filter>
                </defs>

                {/* Ambient twinkles */}
                {content.constellation.ambient.map((s, i) => (
                  <path
                    key={`ambient-${i}`}
                    d={starPath(s.size)}
                    transform={`translate(${s.x},${s.y})`}
                    fill="white"
                    opacity={0}
                    style={{
                      animation: chartVisible
                        ? `astro-twinkle ${s.duration}s ease-in-out infinite ${s.delay}s`
                        : "none",
                    }}
                  />
                ))}

                {/* Constellation line glows (behind the sharp lines) */}
                {content.constellation.stars.map((point, i) => {
                  const next =
                    content.constellation.stars[
                      (i + 1) % content.constellation.stars.length
                    ];
                  return (
                    <line
                      key={`glow-line-${i}`}
                      x1={point.x}
                      y1={point.y}
                      x2={next.x}
                      y2={next.y}
                      stroke={accentColor}
                      strokeWidth="1.5"
                      strokeOpacity={linesDrawn ? 0.15 : 0}
                      filter="url(#astro-line-glow)"
                      strokeDasharray="200"
                      style={{
                        strokeDashoffset: linesDrawn ? 0 : 200,
                        transition: `stroke-dashoffset 1.2s ease-out ${i * 0.15}s, stroke-opacity 0.8s ease-out ${i * 0.15}s`,
                      }}
                    />
                  );
                })}

                {/* Constellation lines (sharp) */}
                {content.constellation.stars.map((point, i) => {
                  const next =
                    content.constellation.stars[
                      (i + 1) % content.constellation.stars.length
                    ];
                  return (
                    <line
                      key={`line-${i}`}
                      x1={point.x}
                      y1={point.y}
                      x2={next.x}
                      y2={next.y}
                      stroke={accentColor}
                      strokeWidth="0.3"
                      strokeOpacity={linesDrawn ? 0.5 : 0}
                      strokeDasharray="200"
                      style={{
                        strokeDashoffset: linesDrawn ? 0 : 200,
                        transition: `stroke-dashoffset 1.2s ease-out ${i * 0.15}s, stroke-opacity 0.5s ease-out ${i * 0.15}s`,
                      }}
                    />
                  );
                })}

                {/* Cross lines (connect non-adjacent stars for a richer web) */}
                {content.constellation.stars.map((point, i) => {
                  const skip =
                    content.constellation.stars[
                      (i + 2) % content.constellation.stars.length
                    ];
                  return (
                    <line
                      key={`cross-${i}`}
                      x1={point.x}
                      y1={point.y}
                      x2={skip.x}
                      y2={skip.y}
                      stroke={accentColor}
                      strokeWidth="0.15"
                      strokeOpacity={linesDrawn ? 0.2 : 0}
                      strokeDasharray="200"
                      style={{
                        strokeDashoffset: linesDrawn ? 0 : 200,
                        transition: `stroke-dashoffset 1.4s ease-out ${0.3 + i * 0.12}s, stroke-opacity 0.6s ease-out ${0.3 + i * 0.12}s`,
                      }}
                    />
                  );
                })}

                {/* Center lines (faint) */}
                {content.constellation.stars.map((point, i) => (
                  <line
                    key={`center-line-${i}`}
                    x1={point.x}
                    y1={point.y}
                    x2={50}
                    y2={50}
                    stroke={accentColor}
                    strokeWidth="0.15"
                    strokeOpacity={linesDrawn ? 0.15 : 0}
                    strokeDasharray="200"
                    style={{
                      strokeDashoffset: linesDrawn ? 0 : 200,
                      transition: `stroke-dashoffset 1s ease-out ${0.5 + i * 0.1}s, stroke-opacity 0.5s ease-out ${0.5 + i * 0.1}s`,
                    }}
                  />
                ))}

                {/* Star glow halos */}
                {content.constellation.stars.map((point, i) => (
                  <circle
                    key={`glow-${i}`}
                    cx={point.x}
                    cy={point.y}
                    r={point.size * 2.5}
                    fill={accentColor}
                    opacity={0}
                    filter="url(#astro-star-glow)"
                    style={{
                      animation: chartVisible
                        ? `astro-glow-pulse ${3.5 + (i % 3)}s ease-in-out infinite ${point.delay}s`
                        : "none",
                    }}
                  />
                ))}

                {/* Star points (4-pointed stars) */}
                {content.constellation.stars.map((point, i) => (
                  <path
                    key={`star-${i}`}
                    d={starPath(point.size)}
                    transform={`translate(${point.x},${point.y})`}
                    fill="white"
                    opacity={chartVisible ? 0.9 : 0}
                    style={{
                      transition: `opacity 0.5s ease-out ${point.delay}s`,
                      animation: chartVisible
                        ? `astro-twinkle ${3 + (i % 3)}s ease-in-out infinite ${point.delay}s`
                        : "none",
                    }}
                  />
                ))}
              </svg>

              {/* Central emoji */}
              <div
                className={cn(
                  "absolute top-1/2 left-1/2 transition-all duration-700",
                  emojiVisible ? "opacity-100 scale-100" : "opacity-0 scale-50",
                )}
                style={{
                  transform: emojiVisible
                    ? "translate(-50%, -50%) scale(1)"
                    : "translate(-50%, -50%) scale(0.5)",
                  fontSize: "clamp(3rem, 12vw, 5rem)",
                  filter: `drop-shadow(0 0 25px ${accentColor}44) drop-shadow(0 0 8px ${accentColor}22)`,
                }}
              >
                {content.emoji}
              </div>
            </div>

            {/* Constellation name below chart */}
            <div
              className={cn(
                "mt-1 text-center transition-opacity duration-700",
                interpretationVisible ? "opacity-100" : "opacity-0",
              )}
              style={{
                animation: interpretationVisible
                  ? "astro-fade-up 0.6s ease-out"
                  : "none",
              }}
            >
              <p className="astro-text text-indigo-300 tracking-widest uppercase text-xs">
                {content.reading.constellation}
              </p>
            </div>

            {/* Pointer hint */}
            <div
              className={cn(
                "mt-3 text-center transition-opacity duration-300",
                canAdvance ? "opacity-100" : "opacity-0",
              )}
            >
              <span
                className="text-indigo-400 pointer-hint-astro inline-block"
                style={{ fontSize: "clamp(1rem, 3vw, 1.25rem)" }}
              >
                ✦
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Farewell — closing message, no constellation */}
      {state === "farewell" && content && (
        <div className="fixed inset-x-0 bottom-[50dvh] md:bottom-[48dvh] z-[85] flex justify-center pointer-events-none">
          <div className="text-center w-[90vw] max-w-lg">
            <p
              className={cn(
                "astro-text-body text-indigo-200/80 leading-relaxed transition-opacity duration-1000",
                farewellVisible ? "opacity-100" : "opacity-0",
              )}
              style={{ fontSize: "clamp(0.95rem, 3vw, 1.25rem)" }}
            >
              {content.closing}
            </p>
            <div
              className={cn(
                "mt-4 transition-opacity duration-300",
                canAdvance ? "opacity-100" : "opacity-0",
              )}
            >
              <span
                className="text-indigo-400 pointer-hint-astro inline-block"
                style={{ fontSize: "clamp(1rem, 3vw, 1.25rem)" }}
              >
                ✦
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
